<template>
  <div class="shortcut-tile-card flex flex-col justify-between" @click.stop="onClickCard">
    <!-- Top accent bar -->
    <div class="shortcut-accent-bar" :style="{ background: hex_color }"></div>

    <div class="p-4 flex flex-col justify-between flex-1">
      <!-- Main Content Header with Date Range Selector on Top -->
      <div class="flex items-center justify-between">
        <div class="flex items-center min-w-0 pr-2">
          <!-- Icon Container -->
          <div
            class="shortcut-icon-wrapper flex items-center justify-center rounded-xl mr-3 flex-shrink-0"
            :style="{ background: rgb_color, color: hex_color, border: `1px solid ${rgb_color_border}` }"
          >
            <feather-icon
              :icon="icon || 'TargetIcon'"
              svgClasses="h-5 w-5"
              :style="{ color: hex_color }"
            />
          </div>

          <!-- Text Info -->
          <div class="min-w-0">
            <h4 class="shortcut-title font-bold text-base mb-0.5 leading-tight truncate" :title="statistic">
              {{ statistic }}
            </h4>
            <p class="shortcut-desc text-xs truncate">
              Regional on-time in-full performance
            </p>
          </div>
        </div>

        <!-- Date Range Selector at the Top Right -->
        <div class="range-selector-top-wrapper relative flex-shrink-0 ml-1" @click.stop>
          <button
            type="button"
            class="range-selector-top-btn flex items-center"
            :class="{ 'is-active': show_dropdown }"
            @click.stop="toggleDropdown"
            title="Select Period"
          >
            <feather-icon icon="CalendarIcon" svgClasses="h-3 w-3 mr-1 text-primary" />
            <span class="range-selected-text">{{ selectedRangeDisplayLabel }}</span>
            <feather-icon icon="ChevronDownIcon" svgClasses="h-3 w-3 ml-1 chevron-arrow" />
          </button>

          <!-- Range Picklist Dropdown Popover -->
          <transition name="dropdown-fade">
            <div
              v-if="show_dropdown"
              class="shortcut-range-dropdown"
              @click.stop
            >
              <div class="range-dropdown-header flex items-center justify-between">
                <span class="range-header-title">Select Period</span>
                <span class="range-header-dates">{{ activeDateSubtitle }}</span>
              </div>

              <!-- Picklist Actions -->
              <div class="range-preset-list">
                <button
                  v-for="preset in presets"
                  :key="preset.key"
                  type="button"
                  class="range-preset-btn flex items-center justify-between w-full"
                  :class="{ 'is-selected': selectedRangeKey === preset.key }"
                  @click.stop="onSelectPreset(preset.key)"
                >
                  <div class="flex items-center min-w-0">
                    <feather-icon
                      :icon="preset.icon"
                      svgClasses="h-3.5 w-3.5 mr-2 flex-shrink-0"
                      :style="selectedRangeKey === preset.key ? { color: hex_color } : {}"
                    />
                    <span class="preset-name text-xs">{{ preset.label }}</span>
                  </div>
                  <span v-if="preset.key !== 'custom'" class="preset-dates-preview text-xs">
                    {{ getPresetRange(preset.key).preview }}
                  </span>
                  <feather-icon
                    v-else
                    icon="ChevronRightIcon"
                    svgClasses="h-3 w-3 text-muted"
                  />
                </button>
              </div>

              <!-- Custom Range Inline Section with From and To datepickers -->
              <div v-if="selectedRangeKey === 'custom'" class="custom-range-picker-section mt-2 pt-2 border-t">
                <div class="mb-2">
                  <label class="custom-range-label text-xs font-semibold mb-1 block">Date From</label>
                  <flat-pickr
                    v-model="tempCustomFrom"
                    :config="flatpickrConfig"
                    class="custom-range-input w-full"
                    placeholder="Choose Date From"
                  />
                </div>
                <div class="mb-2">
                  <label class="custom-range-label text-xs font-semibold mb-1 block">Date To</label>
                  <flat-pickr
                    v-model="tempCustomTo"
                    :config="flatpickrConfig"
                    class="custom-range-input w-full"
                    placeholder="Choose Date To"
                  />
                </div>
                <div class="custom-range-actions flex items-center justify-end mt-2 pt-1">
                  <vs-button
                    size="small"
                    type="flat"
                    color="dark"
                    class="py-1 px-2 text-xs mr-1"
                    @click.stop="cancelCustomRange"
                  >
                    Cancel
                  </vs-button>
                  <vs-button
                    size="small"
                    color="primary"
                    class="py-1 px-3 text-xs"
                    :disabled="!isValidCustomRange"
                    @click.stop="applyCustomRange"
                  >
                    Apply
                  </vs-button>
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>

      <!-- OTIF Regional Metrics Section -->
      <div class="shortcut-stats-row" :class="{ 'is-loading-stats': is_loading }">
        <div
          v-for="region in regions"
          :key="region.key"
          class="shortcut-stat-pill cursor-pointer"
          @click.stop="onClickRegion(region)"
        >
          <span class="stat-pill-label">{{ region.label }}</span>
          <vx-tooltip
            :text="region.targetAvg !== null ? `${region.label} (${tooltipPeriodText}) - Actual: ${formatPercent(region.actual)} (Target: ${region.targetAvg}%) • Click to view ${region.filterValue} report` : `${region.label} (${tooltipPeriodText}) - Actual: ${formatPercent(region.actual)} • Click to view ${region.filterValue} report`"
            position="top"
          >
            <span class="stat-pill-badge" :class="getBadgeClass(region)">
              <span v-if="is_loading" class="loading-placeholder">...</span>
              <span v-else>{{ formatPercent(region.actual) }}</span>
            </span>
          </vx-tooltip>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'
import _color from '@/assets/utils/color.js'
import { RepositoryFactory } from '@/repository/RepositoryFactory'
const OtifTargetRepository = RepositoryFactory.get('otif_target')

export default {
  name: 'OtifLastMonthShortcutCard',
  components: {
    flatPickr
  },
  props: {
    icon: {
      type: String,
      default: 'TargetIcon'
    },
    statistic: {
      type: [Number, String],
      default: 'OTIF'
    },
    statisticTitle: {
      type: String,
      default: ''
    },
    color: {
      type: String,
      default: '#00CFE8',
    },
    url: {
      type: String,
      default: '/pages/otif-report'
    },
    endpoint: {
      type: String,
      default: ''
    },
    is_edit_mode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      is_loading: false,
      show_dropdown: false,
      selectedRangeKey: 'custom',
      appliedRangeKey: 'custom',
      activeFrom: '',
      activeTo: '',
      tempCustomFrom: '',
      tempCustomTo: '',
      presets: [
        { key: 'this_week', label: 'This Week', icon: 'ClockIcon' },
        { key: 'last_week', label: 'Last Week', icon: 'RotateCcwIcon' },
        { key: 'this_month', label: 'This Month', icon: 'CalendarIcon' },
        { key: 'last_month', label: 'Last Month', icon: 'CalendarIcon' },
        { key: 'custom', label: 'Custom Range', icon: 'SlidersIcon' }
      ],
      selectedRegion: '',
      regions: [
        {
          key: 'uk',
          label: 'UK',
          filterValue: 'Great Britain',
          matchNames: ['great britain', 'uk', 'gb', 'united kingdom'],
          actual: null,
          targetAvg: null
        },
        {
          key: 'eu',
          label: 'EU',
          filterValue: 'Europe',
          matchNames: ['europe', 'eu', 'european union'],
          actual: null,
          targetAvg: null
        },
        {
          key: 'row',
          label: 'ROW',
          filterValue: 'ROW',
          matchNames: ['row', 'rest of world', 'rest of the world'],
          actual: null,
          targetAvg: null
        }
      ]
    }
  },
  created() {
    this.initDefaultRange()
  },
  mounted() {
    this.fetchOtifData()
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleOutsideClick)
  },
  methods: {
    initDefaultRange() {
      this.selectedRangeKey = 'custom'
      this.appliedRangeKey = 'custom'
      this.activeFrom = moment().startOf('month').format('DD/MM/YYYY')
      this.activeTo = moment().format('DD/MM/YYYY')
      this.tempCustomFrom = this.activeFrom
      this.tempCustomTo = this.activeTo
    },
    getPresetRange(key) {
      switch (key) {
        case 'this_week':
          return {
            from: moment().startOf('isoWeek').format('DD/MM/YYYY'),
            to: moment().endOf('isoWeek').format('DD/MM/YYYY'),
            preview: `${moment().startOf('isoWeek').format('D MMM')} - ${moment().endOf('isoWeek').format('D MMM')}`
          }
        case 'last_week':
          return {
            from: moment().subtract(1, 'week').startOf('isoWeek').format('DD/MM/YYYY'),
            to: moment().subtract(1, 'week').endOf('isoWeek').format('DD/MM/YYYY'),
            preview: `${moment().subtract(1, 'week').startOf('isoWeek').format('D MMM')} - ${moment().subtract(1, 'week').endOf('isoWeek').format('D MMM')}`
          }
        case 'this_month':
          return {
            from: moment().startOf('month').format('DD/MM/YYYY'),
            to: moment().endOf('month').format('DD/MM/YYYY'),
            preview: `${moment().startOf('month').format('D MMM')} - ${moment().endOf('month').format('D MMM')}`
          }
        case 'last_month':
          return {
            from: moment().subtract(1, 'month').startOf('month').format('DD/MM/YYYY'),
            to: moment().subtract(1, 'month').endOf('month').format('DD/MM/YYYY'),
            preview: `${moment().subtract(1, 'month').startOf('month').format('D MMM')} - ${moment().subtract(1, 'month').endOf('month').format('D MMM')}`
          }
        default:
          return null
      }
    },
    toggleDropdown() {
      this.show_dropdown = !this.show_dropdown
      if (this.show_dropdown && this.selectedRangeKey === 'custom') {
        this.tempCustomFrom = this.activeFrom
        this.tempCustomTo = this.activeTo
      }
    },
    handleOutsideClick(e) {
      if (e && e.target && (e.target.closest('.flatpickr-calendar') || e.target.closest('.range-selector-top-wrapper'))) {
        return
      }
      if (this.selectedRangeKey === 'custom' && this.appliedRangeKey !== 'custom') {
        this.selectedRangeKey = this.appliedRangeKey
      }
      this.show_dropdown = false
    },
    onSelectPreset(key) {
      this.selectedRangeKey = key
      if (key === 'custom') {
        this.tempCustomFrom = this.activeFrom || moment().startOf('month').format('DD/MM/YYYY')
        this.tempCustomTo = this.activeTo || moment().format('DD/MM/YYYY')
        return
      }
      const range = this.getPresetRange(key)
      if (range) {
        this.appliedRangeKey = key
        this.activeFrom = range.from
        this.activeTo = range.to
        this.show_dropdown = false
        this.fetchOtifData()
      }
    },
    applyCustomRange() {
      if (this.tempCustomFrom && this.tempCustomTo) {
        this.appliedRangeKey = 'custom'
        this.activeFrom = this.tempCustomFrom
        this.activeTo = this.tempCustomTo
        this.show_dropdown = false
        this.fetchOtifData()
      }
    },
    cancelCustomRange() {
      this.selectedRangeKey = this.appliedRangeKey
      this.tempCustomFrom = this.activeFrom
      this.tempCustomTo = this.activeTo
      this.show_dropdown = false
    },
    async fetchOtifData() {
      this.is_loading = true
      try {
        const payload = {
          from: this.activeFrom,
          to: this.activeTo
        }
        if (this.activeFrom) {
          payload.month_year = moment(this.activeFrom, 'DD/MM/YYYY').format('MM/YYYY')
        }

        const [reportRes, targetsRes] = await Promise.allSettled([
          OtifTargetRepository.getOtifReportData(payload),
          OtifTargetRepository.getOtifTargets()
        ])

        // Parse Report Data
        let regionsList = []
        if (reportRes.status === 'fulfilled' && reportRes.value && reportRes.value.data) {
          const raw = reportRes.value.data
          if (raw.regions && Array.isArray(raw.regions)) {
            regionsList = raw.regions
          } else if (raw.data && raw.data.regions && Array.isArray(raw.data.regions)) {
            regionsList = raw.data.regions
          } else if (Array.isArray(raw)) {
            regionsList = raw
          } else if (raw.data && Array.isArray(raw.data)) {
            regionsList = raw.data
          }
        }

        // Parse Targets Data
        let targetsData = []
        if (targetsRes.status === 'fulfilled' && targetsRes.value && targetsRes.value.data) {
          const raw = targetsRes.value.data
          targetsData = Array.isArray(raw) ? raw : (raw.data && Array.isArray(raw.data) ? raw.data : (raw.data && raw.data.data ? raw.data.data : []))
        }

        // Process each region
        this.regions.forEach(reg => {
          const reportItem = regionsList.find(item => {
            const name = (item.region || '').trim().toLowerCase()
            return reg.matchNames.includes(name)
          })

          if (reportItem) {
            const val = reportItem.otif_percentage !== undefined ? reportItem.otif_percentage : (reportItem.otif_pct !== undefined ? reportItem.otif_pct : reportItem.otif)
            reg.actual = (val !== null && val !== undefined && !isNaN(Number(val))) ? Number(val) : null
          } else {
            reg.actual = null
          }

          // Calculate average target configured for this region
          const matchingTargets = targetsData.filter(item => {
            const name = (item.region || '').trim().toLowerCase()
            return reg.matchNames.includes(name) && item.target !== undefined && item.target !== null && !isNaN(Number(item.target))
          })

          if (matchingTargets.length > 0) {
            const sum = matchingTargets.reduce((acc, curr) => acc + Number(curr.target), 0)
            reg.targetAvg = Math.round(sum / matchingTargets.length)
          } else {
            reg.targetAvg = null
          }
        })
      } catch (error) {
        console.error('Failed to load OTIF report data:', error)
      } finally {
        this.is_loading = false
      }
    },
    formatPercent(val) {
      if (val === undefined || val === null || isNaN(val)) return '-'
      return `${val}%`
    },
    getBadgeClass(region) {
      if (region.actual === undefined || region.actual === null || isNaN(region.actual)) {
        return 'badge-neutral'
      }
      const actual = Number(region.actual)
      const target = region.targetAvg !== null ? Number(region.targetAvg) : 80

      return actual >= target ? 'badge-success' : 'badge-danger'
    },
    onClickRegion(region) {
      if (this.is_edit_mode) return
      if (this.selectedRangeKey === 'custom' && this.tempCustomFrom && this.tempCustomTo) {
        this.activeFrom = this.tempCustomFrom
        this.activeTo = this.tempCustomTo
      }
      const rawUrl = this.url || '/pages/otif-report'
      const basePath = rawUrl.split('?')[0]
      const query = {
        from: this.activeFrom,
        to: this.activeTo,
        month_year: moment(this.activeFrom, 'DD/MM/YYYY').format('MM/YYYY')
      }
      if (region && region.filterValue) {
        query['filter[region]'] = region.filterValue
      }
      this.$router.push({
        path: basePath,
        query
      }).catch(() => {})
    },
    onClickCard() {
      if (this.is_edit_mode) return
      if (this.selectedRangeKey === 'custom' && this.tempCustomFrom && this.tempCustomTo) {
        this.activeFrom = this.tempCustomFrom
        this.activeTo = this.tempCustomTo
      }
      const rawUrl = this.url || '/pages/otif-report'
      const basePath = rawUrl.split('?')[0]
      const query = {
        from: this.activeFrom,
        to: this.activeTo,
        month_year: moment(this.activeFrom, 'DD/MM/YYYY').format('MM/YYYY')
      }
      if (this.selectedRegion) {
        query['filter[region]'] = this.selectedRegion
      }
      this.$router.push({
        path: basePath,
        query
      }).catch(() => {})
    }
  },
  computed: {
    selectedRangeDisplayLabel() {
      const found = this.presets.find(p => p.key === this.selectedRangeKey)
      if (this.selectedRangeKey === 'custom') {
        if (this.activeFrom && this.activeTo) {
          return `${moment(this.activeFrom, 'DD/MM/YYYY').format('D MMM')} - ${moment(this.activeTo, 'DD/MM/YYYY').format('D MMM')}`
        }
        return 'Custom'
      }
      return found ? found.label : 'Period'
    },
    activeDateSubtitle() {
      if (this.activeFrom && this.activeTo) {
        return `${moment(this.activeFrom, 'DD/MM/YYYY').format('D MMM')} - ${moment(this.activeTo, 'DD/MM/YYYY').format('D MMM YYYY')}`
      }
      return ''
    },
    tooltipPeriodText() {
      return this.activeDateSubtitle || this.selectedRangeDisplayLabel
    },
    isValidCustomRange() {
      return Boolean(this.tempCustomFrom && this.tempCustomTo)
    },
    flatpickrConfig() {
      return {
        dateFormat: 'd/m/Y'
      }
    },
    rgb_color: function () {
      if (_color.isHexColor(this.color)) {
        return _color.getColor(this.color, .12)
      } else {
        return _color.getColor('#00CFE8', .12)
      }
    },
    rgb_color_border: function () {
      if (_color.isHexColor(this.color)) {
        return _color.getColor(this.color, .28)
      } else {
        return _color.getColor('#00CFE8', .28)
      }
    },
    hex_color: function () {
      if (_color.isHexColor(this.color)) {
        return this.color
      } else {
        return '#00CFE8'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.shortcut-tile-card {
  position: relative;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.07);
  box-shadow: 0 3px 12px 0 rgba(0, 0, 0, 0.04);
  transition: all 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  cursor: pointer;
  height: 100%;
  min-height: 84px;
  overflow: visible;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
    border-color: rgba(var(--vs-primary), 0.35);

    .shortcut-icon-wrapper {
      transform: scale(1.06);
    }
  }

  .shortcut-accent-bar {
    height: 3px;
    width: 100%;
    opacity: 0.9;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
  }

  .shortcut-icon-wrapper {
    width: 40px;
    height: 40px;
    transition: transform 0.2s ease;
  }

  .shortcut-title {
    color: #2c2c2c;
  }

  .shortcut-desc {
    color: #727b87;
    line-height: 1.3;
  }

  /* Top Range Selector */
  .range-selector-top-wrapper {
    position: relative;
  }

  .range-selector-top-btn {
    display: inline-flex;
    align-items: center;
    padding: 3px 8px;
    font-size: 11px;
    font-weight: 600;
    color: #5e5873;
    border-radius: 7px;
    background: rgba(0, 0, 0, 0.04);
    border: 1px solid rgba(0, 0, 0, 0.08);
    cursor: pointer;
    line-height: 1.2;
    transition: all 0.15s ease;
    user-select: none;
    outline: none;
    white-space: nowrap;

    &:hover,
    &.is-active {
      background: rgba(var(--vs-primary), 0.12);
      border-color: rgba(var(--vs-primary), 0.35);
      color: rgba(var(--vs-primary), 1);

      .chevron-arrow {
        transform: rotate(180deg);
      }
    }

    .chevron-arrow {
      transition: transform 0.2s ease;
    }
  }

  .shortcut-range-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    width: 250px;
    background: #ffffff;
    border-radius: 10px;
    border: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.18), 0 6px 12px -4px rgba(0, 0, 0, 0.08);
    padding: 8px;
    z-index: 150;
    cursor: default;

    .range-dropdown-header {
      margin-bottom: 6px;
      padding-bottom: 5px;
      border-bottom: 1px solid rgba(0, 0, 0, 0.06);

      .range-header-title {
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        color: #5e5873;
        letter-spacing: 0.3px;
      }

      .range-header-dates {
        font-size: 10px;
        font-weight: 600;
        color: #82868b;
      }
    }

    .range-preset-list {
      display: flex;
      flex-direction: column;
      gap: 2px;

      .range-preset-btn {
        padding: 6px 8px;
        font-size: 11px;
        font-weight: 500;
        text-align: left;
        border-radius: 6px;
        border: none;
        background: transparent;
        color: #5e5873;
        cursor: pointer;
        transition: all 0.15s ease;
        outline: none;

        &:hover {
          background: rgba(var(--vs-primary), 0.08);
          color: rgba(var(--vs-primary), 1);
        }

        &.is-selected {
          background: rgba(var(--vs-primary), 0.12);
          color: rgba(var(--vs-primary), 1);
          font-weight: 700;
        }

        .preset-dates-preview {
          font-size: 10px;
          color: #9aa2ba;
        }
      }
    }

    .custom-range-picker-section {
      border-top-color: rgba(0, 0, 0, 0.06);

      .custom-range-label {
        color: #5e5873;
      }

      .custom-range-input {
        font-size: 11px;
        padding: 5px 8px;
        border: 1px solid rgba(0, 0, 0, 0.15);
        border-radius: 6px;
        outline: none;
        transition: border-color 0.15s ease;

        &:focus {
          border-color: rgba(var(--vs-primary), 1);
        }
      }
    }
  }

  .shortcut-stats-row {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 12px;
    padding-top: 10px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    margin-left: -3px;
    margin-right: -3px;
    gap: 4px;
    transition: opacity 0.2s ease;

    &.is-loading-stats {
      opacity: 0.65;
    }
  }

  .shortcut-stat-pill {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex: 1 1 0%;
    min-width: 0;
    margin: 0;
    padding: 5px 6px;
    border-radius: 8px;
    background: rgba(0, 0, 0, 0.035);
    border: 1px solid rgba(0, 0, 0, 0.04);
    overflow: hidden;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(var(--vs-primary), 0.08);
      border-color: rgba(var(--vs-primary), 0.25);
      transform: translateY(-1px);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    }

    .stat-pill-label {
      color: #7d8793;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      margin-right: 6px;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .stat-pill-badge {
      font-size: 11px;
      font-weight: 700;
      white-space: nowrap;
      padding: 3px 8px;
      border-radius: 6px;
      margin-left: auto;
      text-align: center;
      transition: all 0.2s ease;
      cursor: default;

      &.badge-success {
        background-color: rgba(var(--vs-success), 1) !important;
        color: #ffffff !important;
      }

      &.badge-danger {
        background-color: rgba(var(--vs-danger), 1) !important;
        color: #ffffff !important;
      }

      &.badge-neutral {
        background-color: #82868b;
        color: #ffffff;
      }

      .loading-placeholder {
        font-weight: 400;
        letter-spacing: 1px;
      }
    }
  }
}

/* Dropdown Animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.18s cubic-bezier(0.16, 1, 0.3, 1);
  transform-origin: top right;
}
.dropdown-fade-enter,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.95);
}

/* Dark Theme Support */
::v-deep .theme-dark,
.theme-dark {
  .shortcut-tile-card {
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.3);
    background-color: #10163a;

    &:hover {
      box-shadow: 0 10px 24px rgba(0, 0, 0, 0.5);
      border-color: rgba(0, 207, 232, 0.5);
    }

    .shortcut-title {
      color: #ebeefd;
    }

    .shortcut-desc {
      color: #9aa2ba;
    }

    .range-selector-top-btn {
      background: rgba(255, 255, 255, 0.08);
      border-color: rgba(255, 255, 255, 0.12);
      color: #c2c6dc;

      &:hover,
      &.is-active {
        background: rgba(0, 207, 232, 0.25);
        border-color: rgba(0, 207, 232, 0.5);
        color: #ebeefd;
      }
    }

    .shortcut-range-dropdown {
      background: #1e2746;
      border-color: rgba(255, 255, 255, 0.12);
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.6);

      .range-dropdown-header {
        border-bottom-color: rgba(255, 255, 255, 0.08);

        .range-header-title {
          color: #c2c6dc;
        }

        .range-header-dates {
          color: #82868b;
        }
      }

      .range-preset-list {
        .range-preset-btn {
          color: #c2c6dc;

          &:hover {
            background: rgba(0, 207, 232, 0.18);
            color: #ffffff;
          }

          &.is-selected {
            background: rgba(0, 207, 232, 0.25);
            color: #ffffff;
          }

          .preset-dates-preview {
            color: #7d8793;
          }
        }
      }

      .custom-range-picker-section {
        border-top-color: rgba(255, 255, 255, 0.08);

        .custom-range-label {
          color: #c2c6dc;
        }

        .custom-range-input {
          background: #10163a;
          color: #ebeefd;
          border-color: rgba(255, 255, 255, 0.15);

          &:focus {
            border-color: rgba(0, 207, 232, 0.7);
          }
        }
      }
    }

    .shortcut-stats-row {
      border-top-color: rgba(255, 255, 255, 0.07);
    }

    .shortcut-stat-pill {
      background: rgba(255, 255, 255, 0.05);
      border-color: rgba(255, 255, 255, 0.06);

      .stat-pill-label {
        color: #a3adc2;
      }

      .stat-pill-badge {
        &.badge-success {
          background-color: rgba(var(--vs-success), 1) !important;
          color: #ffffff !important;
        }

        &.badge-danger {
          background-color: rgba(var(--vs-danger), 1) !important;
          color: #ffffff !important;
        }
      }
    }
  }
}
</style>
